/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { jwtDecode } from "jwt-decode";
import AuthService from "../services/AuthService";

const ONE_TIME_LINK_PATHS = new Set(["/forms"]);

function unexpired(token: string, now: number): boolean {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return exp === undefined || exp * 1000 > now;
  } catch {
    return false;
  }
}

/**
 * The session token a one-time link carries, or null when the link has none or carries one that
 * is not a JWT or is already expired, so the page can say the link is broken instead of the app
 * trying it and ending the session. Mail clients glue the sentence's period onto pasted links, so
 * a trailing dot is not part of the token.
 */
export function oneTimeToken(search: string, now: number = Date.now()): string | null {
  const raw = new URLSearchParams(search).get("token");
  if (!raw) return null;
  const token = decodeURIComponent(raw).replace(/\.$/, "");
  return unexpired(token, now) ? token : null;
}

/**
 * A one-time link's token has to replace whatever cookie the browser still holds before the app's
 * first user lookup runs, because a stale cookie fails that lookup, the Apollo error link ends
 * the session, and the link lands on sign-in. Runs from the entry file before React renders.
 */
export function applyOneTimeToken(
  { pathname, search }: { pathname: string; search: string },
  now: number = Date.now()
): boolean {
  if (!ONE_TIME_LINK_PATHS.has(pathname.replace(/\/+$/, ""))) return false;
  const token = oneTimeToken(search, now);
  if (token === null) return false;
  AuthService.setAuthToken(token);
  return true;
}
